import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import useForm from '@/hooks/form/use-form.hook'

import { ApplicationState } from '@/state/root.models'
import {
  TypeRestriction,
  ExpirationType,
  TimeZoneUTCType,
  BonusType,
} from '@/enums'
import { Restriction } from '@/state/bonus-engine/models'
import { BGAMING_PROVIDER_EXTERNAL_ID } from '@/constants'

import { getDateTimeFromIsoUTC, getIsoUTCFromDateTime } from '@/utils/date'
import { authSelector } from '@/state/auth/slices/index.slices'
import { StateSchema } from '@/hooks/form/models'

import {
  getFreeSpinGamesOptions,
  getListOfFreeSpinGames,
  getListOfGames,
  setListGames,
} from '@/utils'
import { restrictionsSchema } from '../components/schema'
import { restrictionsValidator } from '../components/validator'

import useBonusEngineFormActions from './use-be-offers-actions.hook'
import useRestrictionsSportsTypes from './use-restrictions-sports-types.hook'

type Props = {
  bonusType: string
}

const useRestrictions = ({ bonusType }: Props) => {
  const isFreeSpins = bonusType === BonusType.FREE_SPINS

  const { currentBonus, providersCategories, providers, games } = useSelector(
    ({ bonusEngine }: ApplicationState) => bonusEngine,
  )

  const { brand } = useSelector(authSelector)

  const { getAllProvidersAction, getAllGamesByProviderAction } =
    useBonusEngineFormActions()

  const { state, handleOnChange, disable, setFormValues, setDisable } = useForm(
    restrictionsSchema(brand.currencies),
    restrictionsValidator(brand.currencies, bonusType),
    () => {},
  )

  const {
    getSportDefaultValues,
    getProductsDefaultValues,
    getWagerTypeDefaultValues,
  } = useRestrictionsSportsTypes()

  const setCurrentRestrictions = () => {
    const validityDate = currentBonus.bonusExpiration
      ? getDateTimeFromIsoUTC(
          currentBonus.bonusExpiration.toString(),
          TimeZoneUTCType['GMT+00 Coordinated Universal Time'],
        )
      : new Date(new Date().setHours(0, 0, 0, 0))

    let schema: StateSchema = {}

    currentBonus.maxBet && currentBonus.maxBet.length > 0
      ? currentBonus.maxBet.forEach((item) => {
          schema = {
            ...schema,
            [`maxBet_${item.currency}`]: {
              value: item.maxBet > 0 ? item.maxBet?.toString() : '',
              error: '',
            },
          }
        })
      : brand.currencies.forEach((currency) => {
          schema = {
            ...schema,
            [`maxBet_${currency}`]: {
              value: '',
              error: '',
            },
          }
        })

    currentBonus.maxWin && currentBonus.maxWin.length > 0
      ? currentBonus.maxWin.forEach((item) => {
          schema = {
            ...schema,
            [`maxWin_${item.currency}`]: {
              value: item.maxWin > 0 ? item.maxWin?.toString() : '',
              error: '',
            },
          }
        })
      : brand.currencies.forEach((currency) => {
          schema = {
            ...schema,
            [`maxWin_${currency}`]: {
              value: '',
              error: '',
            },
          }
        })
    const providerOptions = providers.map((p) => ({
      label: p.name,
      value: p.id,
    }))

    setFormValues({
      providerCategory: {
        value: '',
        error: '',
      },
      providers: {
        // value: providerOptions,
        //HERE
        value: currentBonus.providers
          ? currentBonus.providers.map((provider) => {
              const newProvider = providers.find(
                (item) => Number(item.externalID) === Number(provider.provider),
              )

              return {
                value: Number(provider.provider),
                label: newProvider?.name || '',
              }
            })
          : [],
        error: '',
      },
      games: {
        value: isFreeSpins
          ? getFreeSpinGamesOptions(currentBonus.freeSpinGames || [], games)
          : currentBonus.restrictions?.games
            ? currentBonus.restrictions?.games.map((game: Restriction) => {
                const newGame = games.find(
                  (item) => item.id === Number(game.validationId),
                )
                return {
                  label: newGame?.name || '',
                  type: Number(game.type),
                  validationId: game.validationId.toString(),
                  action: Number(game.action),
                  gameProviderId: game.gameProviderId,
                }
              })
            : [],
        error: '',
      },
      /* TODO: We could use this rollover type in the future
      products: {
        value: currentBonus.restrictions?.products
          ? currentBonus.restrictions?.products.map((product: Restriction) => {
              return {
                type: product.type,
                validationId: Number(product.validationId),
                action: product.action,
              }
            })
          : [],
        error: '',
      }, */
      sports: {
        value: currentBonus.restrictions?.sports
          ? currentBonus.restrictions?.sports.map((sport: Restriction) => {
              return {
                type: sport.type,
                validationId: sport.validationId,
                action: sport.action,
              }
            })
          : [],
        error: '',
      },
      wagerTypes: {
        value: currentBonus.restrictions?.wagerTypes
          ? currentBonus.restrictions?.wagerTypes.map((wager: Restriction) => {
              return {
                type: wager.type,
                validationId: Number(wager.validationId),
                action: wager.action,
              }
            })
          : [],
        error: '',
      },
      maxLine: {
        value: currentBonus.restrictions?.line?.max,
        error: '',
      },
      minLine: {
        value: currentBonus.restrictions?.line?.min,
        error: '',
      },
      expirationUnit: {
        value:
          currentBonus.bonusExpiration !== null &&
          currentBonus.bonusExpiration !== undefined &&
          !Number.isNaN(Number(currentBonus.bonusExpiration))
            ? currentBonus.bonusExpiration
            : '',
        error: '',
      },
      expirationDate: {
        value:
          currentBonus.bonusExpiration && !Number(currentBonus.bonusExpiration)
            ? validityDate
            : new Date(new Date().setHours(0, 0, 0, 0)),
        error: '',
      },
      expirationType: {
        value:
          currentBonus.bonusExpiration && Number(currentBonus.bonusExpiration)
            ? ExpirationType.TIME_UNIT
            : currentBonus.bonusExpiration === undefined
              ? ExpirationType.TIME_UNIT
              : ExpirationType.SPECIFIC_DATE,
        error: '',
      },
      maxBonusesAllowed: {
        value: currentBonus.maxBonusesAllowed || '',
        error: '',
      },
      ...schema,
    })

    setDisable(false)
  }

  const getFullExpiration = () => {
    return getIsoUTCFromDateTime(state.expirationDate.value, '00:00:00')
  }

  // SPORTS RESTRICTIONS

  const setItem = (type: string, item: Restriction): void => {
    if (type === 'product') {
      const filterList = state.products.value.filter(
        (product: Restriction) => product.validationId !== item.validationId,
      )
      setFormValues({
        ...state,
        products: {
          value: [...filterList, item] || [],
          error: '',
        },
      })
    } else if (type === 'wagerType') {
      const filterList = state.wagerTypes.value.filter(
        (wagerType: Restriction) =>
          wagerType.validationId !== item.validationId,
      )
      setFormValues({
        ...state,
        wagerTypes: {
          value: [...filterList, item] || [],
          error: '',
        },
      })
    } else {
      const filterList = state.sports.value.filter(
        (sport: Restriction) => sport.validationId !== item.validationId,
      )
      setFormValues({
        ...state,
        sports: {
          value: [...filterList, item] || [],
          error: '',
        },
      })
    }
  }

  const handleOnClickSetEnable = (
    type: string,
    value: number | string,
    all?: boolean,
  ) => {
    if (!all) {
      const item = {
        type: Number(
          Object.keys(TypeRestriction)[
            Object.values(TypeRestriction).indexOf(type)
          ],
        ),
        validationId: value,
        action: 1,
      }
      setItem(type, item)
    } else {
      handleUpdateAllItems(type, 1)
    }
  }

  const handleOnClickSetDisable = (
    type: string,
    value: number | string,
    all?: boolean,
  ) => {
    if (!all) {
      const item = {
        type: Number(
          Object.keys(TypeRestriction)[
            Object.values(TypeRestriction).indexOf(type)
          ],
        ),
        validationId: value,
        action: 2,
      }
      setItem(type, item)
    } else {
      handleUpdateAllItems(type, 2)
    }
  }

  const handleUpdateAllItems = (type: string, action: number) => {
    if (type === 'product') {
      const filterList = state.products.value.map((product: Restriction) => {
        return {
          ...product,
          action,
        }
      })
      setFormValues({
        ...state,
        products: {
          value: [...filterList] || [],
          error: '',
        },
      })
    } else if (type === 'wagerType') {
      const filterList = state.wagerTypes.value.map((wager: Restriction) => {
        return {
          ...wager,
          action,
        }
      })
      setFormValues({
        ...state,
        wagerTypes: {
          value: [...filterList] || [],
          error: '',
        },
      })
    } else {
      const filterList = state.sports.value.map((sport: Restriction) => {
        return {
          ...sport,
          action,
        }
      })
      setFormValues({
        ...state,
        sports: {
          value: [...filterList] || [],
          error: '',
        },
      })
    }
  }

  const handleOnClickSetDiscardContribution = (
    type: string,
    value: number | string,
  ) => {
    const item = {
      type: Number(
        Object.keys(TypeRestriction)[
          Object.values(TypeRestriction).indexOf(type)
        ],
      ),
      validationId: value,
      action: 3,
    }
    setItem(type, item)
  }

  // CASINO RESTRICTIONS

  const getProviders = () => {
    getAllProvidersAction({ category: 1 }) // default 4 category of games
  }

  const handleOnChangeCategory = (name: string, value: any) => {
    handleOnChange(name, value)
    getProviders()
  }

  const handleOnChangeProvider = (name: string, values: any) => {
    handleOnChange(name, values)
  }

  const setProvidersState = () => {
    if (
      providers !== undefined &&
      providers.length > 0 &&
      currentBonus.providers
    ) {
      // const newProviders = currentBonus.providers.map((item: any) => {
      //   return providers.find((data: any) => data.id === item.provider)
      // })
      const newProviders = currentBonus.providers.map((item: any) => {
        return providers.find((data: any) => data.id === item.provider)
      })

      const listProviders = newProviders
        ? newProviders
            .filter(
              (item: any) => item !== undefined && item.name !== undefined,
            )
            .map((provider: any) => {
              return {
                label: provider.name,
                value: provider.externalID,
              }
            })
        : []

      setFormValues({
        ...state,
        providers: {
          value: listProviders || [],
          error: '',
        },
      })
    }
  }

  // useEffect(() => {
  //   setProvidersState()
  // }, [providers]) // eslint-disable-line

  useEffect(() => {
    if (providers.length > 0 && currentBonus.providers) {
      setCurrentRestrictions()
    }
  }, [providers, currentBonus])

  useEffect(() => {
    getProviders()
  }, []) // eslint-disable-line

  useEffect(() => {
    if (
      state.sports.value.length === 0 ||
      state.wagerTypes.value.length === 0
    ) {
      setFormValues({
        ...state,
        sports: {
          value: getSportDefaultValues(true) || [],
          error: '',
        },
        wagerTypes: {
          value: getWagerTypeDefaultValues(true) || [],
          error: '',
        },
      })
    }
  }, [state.sports, state.wagerTypes]) // eslint-disable-line

  useEffect(() => {
    //HERE
    //if (games.length === 0 && state.providers.value[0]?.value === 15) {
    if (games.length === 0 && state.providers.value[0]?.value) {
      getAllGamesByProviderAction({ provider: state.providers.value[0].value })
    }
  }, [state.providers.value]) // eslint-disable-line

  const getListGamesBetSoft = () => {
    return getListOfGames(state.games)
  }

  const getListFreeSpinGames = () => {
    return getListOfFreeSpinGames(state.games)
  }

  // free spins can only be issued through BGaming, so the provider is not a choice
  useEffect(() => {
    if (!isFreeSpins || providers.length === 0) return

    const bgaming = providers.find(
      (provider: any) =>
        Number(provider.externalID) === BGAMING_PROVIDER_EXTERNAL_ID,
    )

    if (!bgaming || state.providers.value.length > 0) return

    handleOnChange('providers', [
      { label: bgaming.name, value: Number(bgaming.externalID) },
    ])
  }, [isFreeSpins, providers, state.providers.value]) // eslint-disable-line

  useEffect(() => {
    if (games.length === 0) return

    if (isFreeSpins) {
      // relabel the saved game codes once the games of the provider are known
      const gameCodes =
        state.games.value.length > 0
          ? state.games.value.map((game: any) => game.gameCode)
          : currentBonus.freeSpinGames || []

      setFormValues({
        ...state,
        games: {
          value: getFreeSpinGamesOptions(gameCodes, games),
          error: '',
        },
      })
      return
    }

    setListGames(
      setFormValues,
      currentBonus.restrictions?.games || [],
      games,
      state,
    )
  }, [games]) // eslint-disable-line

  return {
    restrictions: {
      state,
      handleOnChange,
      disable,
      setFormValues,
      setDisable,
    },
    // setCurrentRestrictions,
    handleOnClickSetEnable,
    handleOnClickSetDisable,
    handleOnClickSetDiscardContribution,
    handleOnChangeCategory,
    handleOnChangeProvider,
    getFullExpiration,
    getProductsDefaultValues,
    getListGamesBetSoft,
    getListFreeSpinGames,
    providersCategories,
    providers,
  }
}

export default useRestrictions
